import { useRef } from 'react'
import { motion, useMotionValue, useTransform, type PanInfo } from 'framer-motion'
import type { Combination } from './combinationGenerator'

const SWIPE_THRESHOLD = 100 // px displacement to trigger
const VELOCITY_THRESHOLD = 400 // px/s velocity to trigger
const UP_THRESHOLD = -80

export type SwipeDirection = 'left' | 'right' | 'up'

interface SwipeCardProps {
  combination: Combination
  baseImage: string
  onSwipe: (direction: SwipeDirection) => void
  /** Position in stack: 0 = front, 1 = back-1, 2 = back-2 */
  stackIndex: number
  active: boolean
}

export function SwipeCard({ combination, baseImage, onSwipe, stackIndex, active }: SwipeCardProps) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const rotate = useTransform(x, [-300, 300], [-18, 18])
  const likeOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1])
  const nopeOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0])
  const superLikeOpacity = useTransform(y, [UP_THRESHOLD, 0], [1, 0])

  const exitingRef = useRef(false)

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (exitingRef.current) return

    const { offset, velocity } = info

    // Swipe up = super like
    if (offset.y < UP_THRESHOLD || velocity.y < -VELOCITY_THRESHOLD) {
      exitingRef.current = true
      onSwipe('up')
      return
    }

    // Swipe right = like
    if (offset.x > SWIPE_THRESHOLD || velocity.x > VELOCITY_THRESHOLD) {
      exitingRef.current = true
      onSwipe('right')
      return
    }

    // Swipe left = dislike
    if (offset.x < -SWIPE_THRESHOLD || velocity.x < -VELOCITY_THRESHOLD) {
      exitingRef.current = true
      onSwipe('left')
      return
    }

    // Snap back
  }

  // Scale/position for stacked cards behind the front card
  const stackScale = 1 - stackIndex * 0.05
  const stackY = stackIndex * 12

  const cardClasses = "swiper-card absolute inset-0"

  if (!active) {
    return (
      <motion.div
        className={cardClasses}
        style={{ zIndex: 3 - stackIndex }}
        animate={{ scale: stackScale, y: stackY }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <CardContent combination={combination} baseImage={baseImage} />
      </motion.div>
    )
  }

  return (
    <motion.div
      className={cardClasses}
      style={{ x, y, rotate, zIndex: 3 - stackIndex }}
      drag
      dragElastic={0.7}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      onDragEnd={handleDragEnd}
      exit={{ opacity: 0, transition: { duration: 0.2 } }}
    >
      <CardContent combination={combination} baseImage={baseImage} />

      {/* LIKE overlay */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: likeOpacity, zIndex: 100 }}
      >
        <span className="text-4xl font-black tracking-wider text-green-500 border-[4px] border-green-500 rounded-xl px-5 py-2 -rotate-12 bg-white/80">
          LIKE
        </span>
      </motion.div>

      {/* NOPE overlay */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: nopeOpacity, zIndex: 100 }}
      >
        <span className="text-4xl font-black tracking-wider text-red-500 border-[4px] border-red-500 rounded-xl px-5 py-2 rotate-12 bg-white/80">
          NOPE
        </span>
      </motion.div>

      {/* SUPER LIKE overlay */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: superLikeOpacity, zIndex: 100 }}
      >
        <span className="text-4xl font-black tracking-wider text-yellow-500 border-[4px] border-yellow-500 rounded-xl px-5 py-2 bg-white/80">
          SUPER
        </span>
      </motion.div>
    </motion.div>
  )
}

/** Renders the layered product images inside a card */
function CardContent({ combination, baseImage }: { combination: Combination; baseImage: string }) {
  return (
    <div className="relative w-full h-full">
      {/* Base image */}
      {baseImage && (
        <img
          src={baseImage}
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
          style={{ zIndex: 0 }}
          draggable={false}
        />
      )}

      {/* Product layers */}
      {combination.products.map(({ product }) =>
        product.image ? (
          <img
            key={product.id}
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ zIndex: product.layer, pointerEvents: 'none' }}
            draggable={false}
          />
        ) : null
      )}
    </div>
  )
}
